/**
 * Brain Dump API client
 */

import { fetchApi, getApiBase, getAuthHeaders } from './fetch';

export interface BrainDumpTask {
  content: string;
  dueDate: string | null;
  priority: number;
  projectId: string | null;
  project: string | null;
}

export interface BrainDumpResult {
  transcript: string;
  tasks: BrainDumpTask[];
}

export interface BatchCreateResult {
  created: number;
  tasks: Array<{
    id: string;
    content: string;
    projectId: string | null;
    priority: number;
    dueDate: string | null;
  }>;
}

const MAX_AUDIO_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

/**
 * Send audio blob to the brain dump endpoint.
 * Uses FormData (multipart) so bypasses fetchApi's JSON Content-Type header.
 */
export async function submitBrainDump(audioBlob: Blob): Promise<BrainDumpResult> {
  if (audioBlob.size > MAX_AUDIO_SIZE_BYTES) {
    throw new Error(
      `Recording is too large (${(audioBlob.size / 1024 / 1024).toFixed(1)} MB). Please keep recordings under 50 MB.`,
    );
  }

  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');

  const apiBase = getApiBase();
  const authHeaders = getAuthHeaders();

  const res = await fetch(`${apiBase}/ai/brain-dump`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders, // no Content-Type — browser sets multipart boundary automatically
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any)?.message ?? `Brain dump failed: ${res.status}`);
  }

  return res.json();
}

/**
 * Create multiple tasks in one request.
 */
export async function batchCreateTasks(
  tasks: Array<{
    content: string;
    dueDate?: string | null;
    priority?: number;
    projectId?: string | null;
  }>,
): Promise<BatchCreateResult> {
  return fetchApi<BatchCreateResult>('/tasks/batch', {
    method: 'POST',
    body: JSON.stringify({
      tasks: tasks.map((t) => ({
        content: t.content,
        ...(t.dueDate ? { dueDate: t.dueDate } : {}),
        ...(t.priority != null ? { priority: t.priority } : {}),
        ...(t.projectId ? { projectId: t.projectId } : {}),
      })),
    }),
  });
}
